import { rem } from 'polished'
import React, { useCallback } from 'react'
import { Controller, useController, useFormContext } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import AddressDisplay from './Fields/AddressDisplay'
import {
  priceFieldProps,
  priceRegisterConfig,
  useFieldValidation,
} from './Fields/config'
import ConfirmedBooking from './Fields/ConfirmedBooking'
import RHFDatePickerInput from './Fields/RHFDatePickerInput'
import Input from './Fields/RHFInput'
import TextArea from './Fields/RHFTextArea'
import ShowMore from './Fields/ShowMore'
import TimeInput from './Fields/TimeInput'
import { useDefaultDatePickerMonthYear } from './Hooks/useDefaultDates'
import useEnsureGeoPlace from './Hooks/useEnsureGeoPlace'
import type { CreateOtherTransportItemViewModel } from '../Create/models'
import type { PatchOtherTransportItemViewModel } from '../Edit/models'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import type {
  TripAddressAutocompleteOption,
} from 'tripPlanner/components/AutocompleteInputs/TripAddressAutocomplete'
import TripAddressAutocomplete from 'tripPlanner/components/AutocompleteInputs/TripAddressAutocomplete'
import type { AutocompletePlaceParam } from 'tripPlanner/types/google'
import {
  ADDRESS_RESPONSE_DETAILS,
} from 'tripPlanner/types/google'
import { convertGoogleAddressComponentsToRegion } from 'tripPlanner/utils'

const EndPlaceContainer = styled.div`
  padding-bottom: ${rem(8)};
`

const DividerContainer = styled.div`
  height: ${rem(24)};
`

const PriceInput = styled(Input)`
  width: ${rem(100)};
`

const AUTOCOMPLETE_TYPES: AutocompletePlaceParam = []

interface Props {
  tripId: string
}

const START_GEO_DEP_KEYS: Array<string> = ['startPlace.name']
const END_GEO_DEP_KEYS: Array<string> = ['endPlace.name']

function TransportFields({ tripId }: Props) {
  const { required, urlRegisterConfig } = useFieldValidation()
  const intl = useIntl()
  const methods = useFormContext<
    CreateOtherTransportItemViewModel | PatchOtherTransportItemViewModel
  >()

  const { control, watch, register, setValue, clearErrors } = methods

  useEnsureGeoPlace(methods, 'startPlace', START_GEO_DEP_KEYS)
  useEnsureGeoPlace(methods, 'endPlace', END_GEO_DEP_KEYS)

  const startPlace = watch('startPlace')
  const endPlace = watch('endPlace')

  const handleAddressChange = useCallback(
    (
      placeName: 'startPlace' | 'endPlace',
      selectedOption: TripAddressAutocompleteOption,
    ) => {
      setValue(`${placeName}.name`, selectedOption.label)
      setValue(`${placeName}.type`, 'GEO')

      if (selectedOption.details) {
        const { geometry, formatted_address, place_id, address_components } =
          selectedOption.details

        if (address_components) {
          const region =
            convertGoogleAddressComponentsToRegion(address_components)
          if (region) {
            setValue(`${placeName}.region`, region)
          }
        }

        setValue(`${placeName}.id`, place_id)
        setValue(`${placeName}.address`, formatted_address)

        if (geometry?.location) {
          setValue(`${placeName}.point.lat`, geometry.location.lat)
          setValue(`${placeName}.point.lng`, geometry.location.lng)
        }
      }
    },
    [setValue],
  )

  const handleStartAddressChange = useCallback(
    (selectedOption: TripAddressAutocompleteOption) => {
      handleAddressChange('startPlace', selectedOption)
    },
    [handleAddressChange],
  )

  const handleEndAddressChange = useCallback(
    (selectedOption: TripAddressAutocompleteOption) => {
      handleAddressChange('endPlace', selectedOption)
    },
    [handleAddressChange],
  )

  const {
    fieldState: { error: endPlaceError },
  } = useController({ name: 'endPlace' })

  const handleSetStartPlaceToEndPlace = useCallback(() => {
    setValue('endPlace', startPlace)

    // React hook form not recognizing valid field after this update
    if (endPlaceError) {
      clearErrors('endPlace')
    }
  }, [setValue, startPlace, clearErrors, endPlaceError])

  const { defaultMonth, defaultYear } = useDefaultDatePickerMonthYear({
    tripId,
  })

  const startHeading = intl.formatMessage({
    id: 'q+Kwf7n0zr',
    defaultMessage: 'Start',
    description: 'Section heading for start location in transport trip item editor',
  })

  const startLocationLabel = intl.formatMessage({
    id: 'B8lYkP4TRC',
    defaultMessage: 'Start location',
    description: 'Form label for start location field in transport trip item editor',
  })

  const searchLocationPlaceholder = intl.formatMessage({
    id: 'SzSVsvQ7if',
    defaultMessage: 'Search for location',
    description: 'Placeholder text for location search input in transport trip item editor',
  })

  const addressLabel = intl.formatMessage({
    id: 'NMe5ftouV6',
    defaultMessage: 'Address',
    description: 'Label for address display in transport trip item',
  })

  const pickUpDateLabel = intl.formatMessage({
    id: 'xzG24LfTqH',
    defaultMessage: 'Pick up date',
    description: 'Form label for pick up date field in transport trip item editor',
  })

  const timeLabel = intl.formatMessage({
    id: 'Qpbsvf7fBC',
    defaultMessage: 'Time',
    description: 'Form label for time field in transport trip item editor',
  })

  const selectTimePlaceholder = intl.formatMessage({
    id: 'DfqdAkubDY',
    defaultMessage: 'Select time',
    description: 'Placeholder text for time picker in transport trip item editor',
  })

  const endHeading = intl.formatMessage({
    id: 'QZA2JQHzTG',
    defaultMessage: 'End',
    description: 'Section heading for end location in transport trip item editor',
  })

  const endLocationLabel = intl.formatMessage({
    id: 'qU8FpaURo8',
    defaultMessage: 'End location',
    description: 'Form label for end location field in transport trip item editor',
  })

  const dropOffDateLabel = intl.formatMessage({
    id: 'OZaa+Kp5go',
    defaultMessage: 'Drop-off date',
    description: 'Form label for drop-off date field in transport trip item editor',
  })

  const operatorLabel = intl.formatMessage({
    id: 'plhUrgo8Le',
    defaultMessage: 'Operator',
    description: 'Form label for operator name field in transport trip item editor',
  })

  const operatorPlaceholder = intl.formatMessage({
    id: 'harJsFU/9D',
    defaultMessage: 'Enter an operator',
    description: 'Placeholder text for operator input in transport trip item editor',
  })

  const priceLabel = intl.formatMessage({
    id: 'SbXYTLoWSi',
    defaultMessage: 'Price',
    description: 'Form label for price field in transport trip item editor',
  })

  const websiteLabel = intl.formatMessage({
    id: 'gvbTw/29k0',
    defaultMessage: 'Website',
    description: 'Form label for website URL field in transport trip item editor',
  })

  const noteLabel = intl.formatMessage({
    id: 'QJeyFbeHS/',
    defaultMessage: 'Note',
    description: 'Form label for notes field in transport trip item editor',
  })

  const pricePlaceholder = intl.formatMessage({
    id: 'KR9iVlSSmf',
    defaultMessage: '0.00',
    description: 'Placeholder for price input field',
  })

  const websitePlaceholder = intl.formatMessage({
    id: 'vfPwnopWHV',
    defaultMessage: 'https://',
    description: 'Placeholder for website URL input field',
  })

  const notePlaceholder = intl.formatMessage({
    id: '8oYZw5S1dM',
    defaultMessage: 'Add note here',
    description: 'Placeholder for notes textarea field',
  })

  const copyAddressHelpText = <BodyText variant="medium">
    <FormattedMessage
      id="HkWcOYhgRm"
      defaultMessage="Add same address as <link>pick up location</link>"
      description="Link to copy start address to end address in transport form"
      values={{
        link: (chunks) => <TextLink onClick={handleSetStartPlaceToEndPlace} underline={false}>{chunks}</TextLink>,
      }}
    />
  </BodyText>

  return (
    <VerticalSpacer gap={16}>
      <Heading variant="heading4">{startHeading}</Heading>

      <TripAddressAutocomplete
        {...register('startPlace.name', { required })}
        required
        label={startLocationLabel}
        placeholder={searchLocationPlaceholder}
        initPlaceId={startPlace?.type === 'GEO' ? startPlace.id : undefined}
        autocompleteTypes={AUTOCOMPLETE_TYPES}
        details={ADDRESS_RESPONSE_DETAILS}
        onPlaceSelect={handleStartAddressChange}
        startIcon={<LineMapMarkerIcon />}
      />
      {startPlace?.address && (
        <AddressDisplay title={addressLabel} value={startPlace.address} />
      )}

      <Group direction="horizontal" horizontalAlign="stretch" gap={16}>
        <RHFDatePickerInput
          name="startDate"
          label={pickUpDateLabel}
          defaultMonth={defaultMonth}
          defaultYear={defaultYear}
        />
        <TimeInput
          {...register('startTime')}
          label={timeLabel}
          placeholder={selectTimePlaceholder}
        />
      </Group>

      <DividerContainer>
        <Divider kind="primary" />
      </DividerContainer>

      <Heading variant="heading4">{endHeading}</Heading>

      <EndPlaceContainer>
        <TripAddressAutocomplete
          {...register('endPlace.name', { required })}
          required
          label={endLocationLabel}
          placeholder={searchLocationPlaceholder}
          initPlaceId={endPlace?.type === 'GEO' ? endPlace.id : undefined}
          autocompleteTypes={AUTOCOMPLETE_TYPES}
          details={ADDRESS_RESPONSE_DETAILS}
          onPlaceSelect={handleEndAddressChange}
          startIcon={<LineMapMarkerIcon />}
          helpText={copyAddressHelpText}
        />
        {endPlaceError && copyAddressHelpText}
      </EndPlaceContainer>

      {endPlace?.address && (
        <AddressDisplay title={addressLabel} value={endPlace.address} />
      )}

      <Group direction="horizontal" horizontalAlign="stretch" gap={16}>
        <RHFDatePickerInput
          name="endDate"
          label={dropOffDateLabel}
          defaultMonth={defaultMonth}
          defaultYear={defaultYear}
        />
        <TimeInput
          {...register('endTime')}
          label={timeLabel}
          placeholder={selectTimePlaceholder}
        />
      </Group>

      <DividerContainer>
        <Divider kind="primary" />
      </DividerContainer>

      <Controller
        name="isBooked"
        control={control}
        render={({ field }) => (
          <ConfirmedBooking
            toggled={!!field.value}
            onToggle={() => field.onChange(!field.value)}
          />
        )}
      />

      <ShowMore>
        <Input
          {...register('operatorName')}
          label={operatorLabel}
          placeholder={operatorPlaceholder}
        />

        <PriceInput
          {...register('price', { ...priceRegisterConfig })}
          label={priceLabel}
          placeholder={pricePlaceholder}
          {...priceFieldProps}
        />

        <Input
          {...register('url', { ...urlRegisterConfig })}
          label={websiteLabel}
          placeholder={websitePlaceholder}
        />

        <TextArea
          {...register('notes')}
          placeholder={notePlaceholder}
          label={noteLabel}
          resize="vertical"
          size="tall"
        />
      </ShowMore>
    </VerticalSpacer>
  )
}

export default TransportFields
